<template>
  <div>
    <!-- BUTTON -->
    <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events -->
    <Component
      :is="hasAlternates ||isProductPage ? 'button': 'div'"
      class="flex w-full items-center gap-3 text-grey-300"
      :class="{ 'hover:md:text-primary-900 md:transition-colors md:ease-in-out': hasAlternates }"
      :aria-expanded="hasAlternates||isProductPage ? isDropdownOpen : null"
      :aria-controls="hasAlternates||isProductPage ? 'lang-switcher-dropdown': null"
      @click="toggleDropdown"
    >
      <!-- Globe Icon -->
      <IconLanguage class="size-6" />

      <!-- Current locale -->
      <span class="mt-px leading-none typo-subtitle">
        {{ locales.find(locale => locale.code === currentLocale)?.name }}
      </span>

      <IconChevron
        v-if="hasAlternates"
        class="ml-auto w-7 shrink-0 transition-transform duration-300 ease-in-out"
        :class="{ 'rotate-180': isDropdownOpen }"
      />
    </Component>

    <!-- DROPDOWN -->
    <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events -->
    <div
      id="lang-switcher-dropdown"
      ref="dropdownElement"
      class="relative overflow-hidden transition-[width,height] duration-500 ease-in-out"
    >
      <div ref="dropdownContentElement" class="py-8">
        <!-- Title -->
        <div class="font-semibold typo-subtitle slide-right">
          {{ langSwitcher?.title_lang_switcher ? langSwitcher.title_lang_switcher : 'Dieser Titel kann in den Globalen Inhalten definiert werden.' }}
        </div>

        <ul
          class="
            mt-6 space-y-8
            after:absolute after:inset-x-0 after:bottom-0 after:h-10 after:bg-gradient-to-b
            after:from-transparent after:to-white
          "
        >
          <li v-for="[key, localesGroup] in Object.entries(localesByGroup)" :key="key">
            <!-- Group Title -->
            <div class="mb-3 font-semibold typo-subtitle">
              {{ $t(`lang_switcher.${key}`) }}
            </div>

            <!-- Languages -->
            <!-- TODO: locale.code -> localce.path ? -->
            <ul class="space-y-3">
              <li v-for="locale in localesGroup" :key="locale.code">
                <button
                  type="button"
                  :aria-label="$t('switch_language')"
                  class="transition-colors ease-in-out hover:text-primary-900"
                  :class="{ 'text-primary-900': locale.code === currentLocale }"
                  @click="switchLanguage(locale.code)"
                >
                  {{ locale.display }}
                </button>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
type Alternate = {
  path: string
  locale: string
  url?: string
  permalink?: string
}

const switchLocalePath = useSwitchLocalePath()
const { locale: currentLocale, locales } = useI18n()
const route = useRoute()
const entry = inject(EntryInjectionKey, null)
const globals = inject(GlobalsKey, null)

const isDropdownOpen = ref(false)
const dropdownElement = ref<HTMLElement | null>(null)
const dropdownContentElement = ref<HTMLElement | null>(null)

const langSwitcher = computed(() =>
  globals?.value.lang_switcher as {
    title_lang_switcher?: string | null
    handle: string
  } | null)

const isProductPage = computed(() => {
  return route.name?.startsWith('products-all')
})

const alternates = computed<Alternate[]>(() => {
  return Array.isArray(entry?.value?.alternates) ? entry.value.alternates.filter(e => !!e) as Alternate[] : []
})

const hasAlternates = computed(() => alternates.value?.length > 1)

const localesByGroup = computed(() => {
  let filteredLocales: any[] = []

  if (isProductPage.value) {
    // If it's a product page, show all locales
    filteredLocales = locales.value
  } else {
    // For all other pages filter locales that match the alternate codes
    if (!hasAlternates.value) {
      return []
    }
    // Extract locale codes from alternates
    const alternatePaths = alternates.value?.map(alt => alt.path)
    filteredLocales = locales.value.filter(locale => alternatePaths?.includes(locale.code))
  }

  // Group the filtered locales by their 'group'
  return filteredLocales?.reduce((acc, locale) => {
    if (!acc[locale.group]) {
      acc[locale.group] = []
    }

    acc[locale.group].push(locale)
    return acc
  }, {} as Record<string, any[]>)
})

function toggleDropdown() {
  // Toggle the dropdown
  if (!dropdownElement.value || !dropdownContentElement.value) return

  isDropdownOpen.value = !isDropdownOpen.value

  if (isDropdownOpen.value) {
    dropdownElement.value.style.height = `${dropdownContentElement.value.offsetHeight}px`
    emit('heightChange', dropdownContentElement.value.offsetHeight)
  } else {
    dropdownElement.value.style.height = '0px'
    emit('heightChange', -dropdownContentElement.value.offsetHeight)
  }
}

function switchLanguage(selectedPath: string) {
  if (!isProductPage.value && Array.isArray(alternates.value) && alternates.value.length) {
    const url = alternates.value.find(a => a.path === selectedPath)?.permalink

    if (url && url !== route.path) {
      switchLocalePath(selectedPath) // this is needed to work with different domains for different languages
      return navigateTo(url, {
        external: true,
      })
    }
  }

  navigateTo(switchLocalePath(selectedPath), {
    external: true, // this is needed to work with different domains for different languages
  })
}

onMounted(() => {
  if (dropdownElement.value) {
    dropdownElement.value.style.height = 0
  }
})

const emit = defineEmits(['heightChange'])
</script>
